import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * IncomeAnalysis Widget
 */
@Component({
  selector: 'income-analysis-widget',
  templateUrl: './income-analysis-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './income-analysis-widget.component.scss']
})
export class IncomeAnalysisWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart categories
   */
  categories: any[];

  /**
   * Chart TenancyProfile series
   */
  seriesTenancyProfile: any[];

  /**
   * Chart TenancyProfileLine series
   */
  seriesTenancyProfileLine: any[];

  /**
   * Chart LeaseExpiry series
   */
  seriesLeaseExpiry: any[];

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'incomeAnalysis', false);
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    const self = this;//Kendo - keep it
    this.textAreaOptions = {
      change: function () {
        self.setDirtyFlag(true);
      },
      update: function (text) {
        self.widgetData.Comment = text;
      },
    };

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("incomeAnalysis", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('incomeAnalysis').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('incomeAnalysis', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    this.reportDesignerTranslator.ready('incomeAnalysis').then(() => {
      this.seriesTenancyProfile = [
        {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelRenewalRent'),
          color: '#636468',
          data: data.years.map((entry) => entry.renewalRent)
        },
        {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelSecuredRent'),
          color: '#DBD6C8',
          data: data.years.map((entry) => entry.securedRent)
        }
      ];

      this.seriesTenancyProfileLine = [
        {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelGrossMarketRent'),
          color: '#E30613',
          data: data.years.map((entry) => entry.grossMarketRent)
        }
      ];

      this.seriesLeaseExpiry = [
        {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelInitialExpiries'),
          color: '#636468',
          data: data.years.map((entry) => entry.initialExpiries)
        }, {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelRenewals'),
          color: '#DBD6C8',
          data: data.years.map((entry) => entry.renewals)
        },
        {
          name: this.reportDesignerTranslator.getString('incomeAnalysis.labelVacancy'),
          color: '#E30613',
          data: data.years.map((entry) => entry.vacancy)
        }
      ];

      this.categories = data.years.map((entry) => entry.periodDescription);

      this.chartsReady = true;
    });

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Checks if value is valid year
   * @param value {any} Value
   */
  private isValidYear(value) {
    return !(value == null || value == "" || value == "0");
  }

  /**
   * Returns chart Area label content
  * @param e {any} Event object
   */
  chartLabelContentArea(e: any): string {
    return `${e.dataItem.percentage}`;
  }

  /**
   * Returns chart Rent label content
  * @param e {any} Event object
   */
  chartLabelContentRent(e: any): string {
    return `${e.dataItem.percentage}`;
  }

  /**
   * Returns formatted Year of Construction
   */
  getYearOfConstructionFormatted(): string {
    const value = this.widgetData.yearOfConstruction;
    const value2 = this.widgetData.yearOfConstruction2;
    const value3 = this.widgetData.yearOfConstruction3;
    const value4 = this.widgetData.yearOfConstruction4;
    const value5 = this.widgetData.yearOfConstruction5;

    if (!this.isValidYear(value)) {
      return this.reportDesignerTranslator.getString('common.notAvailable');
    } else {
      let str = "";
      str += (this.isValidYear(value5) ? (value5 + " / ") : "");
      str += (this.isValidYear(value4) ? (value4 + " / ") : "");
      str += (this.isValidYear(value3) ? (value3 + " / ") : "");
      str += (this.isValidYear(value2) ? (value2 + " / ") : "");
      str += (this.isValidYear(value) ? (value) : "");

      return str;
    }
  }

  /**
   * Returns formatted Year of Refurbishment
   */
  getYearOfRefurbishmentFormatted(): string {
    const value = this.widgetData.yearOfRefurbishment;
    const value2 = this.widgetData.yearOfRefurbishment2;
    const value3 = this.widgetData.yearOfRefurbishment3;
    const value4 = this.widgetData.yearOfRefurbishment4;
    const value5 = this.widgetData.yearOfRefurbishment5;

    if (!this.isValidYear(value)) {
      return this.reportDesignerTranslator.getString('common.notAvailable');
    } else {
      let str = "";
      str += (this.isValidYear(value5) ? (value5 + " / ") : "");
      str += (this.isValidYear(value4) ? (value4 + " / ") : "");
      str += (this.isValidYear(value3) ? (value3 + " / ") : "");
      str += (this.isValidYear(value2) ? (value2 + " / ") : "");
      str += (this.isValidYear(value) ? (value) : "");

      return str;
    }
  }
}
